import { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import axios from "axios";

const SchoolConstants = () => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [error, setError] = useState(null);

  // Get token from Redux auth state
  const { user } = useSelector((state) => state.auth || {});
  const token = user?.token;

  // We need to fetch ALL thawabet records.
  // Currently, the backend endpoint fetchThawabetCtrl is designed to fetch by rgmw.
  // To display a table of ALL school constants, we need a new endpoint or update the existing one
  // to return all records if no rgmw is provided, or a dedicated endpoint.
  // Assuming the user wants to see a list of all schools' constants.

  // However, based on previous context, the user asked to "edit the table to bring all fields in thawabet table".
  // And "make a page called School Constants and put all data from this table in it".

  // Since we modified fetchThawabetCtrl to filter by rgmw, we might need another endpoint
  // OR we can iterate over all schools and fetch their constants (inefficient)
  // OR ideally, create a new endpoint /fetchAllThawabet.

  // For now, I will try to fetch for all schools if possible, but let's assume we need a new endpoint.
  // But I cannot easily add a new endpoint without user permission to edit backend further for a *new* list endpoint.
  // The user said "Update the table to bring ALL fields".

  // Let's implement the UI first. We will try to fetch data.
  // If we don't have a "fetch all" endpoint, we can't display all schools' constants efficiently.
  // BUT, maybe the user meant "for a specific school"?
  // "We want to make a page called School Constants and put all data from this table in it".
  // This usually implies a dashboard page listing all rows.

  // Let's assume we need to fetch all. I will use a hypothetical endpoint or existing one if modified.
  // Wait, I previously modified fetchThawabetCtrl to FILTER by rgmw.
  // To get ALL, I should probably add a new endpoint or modify the existing one to handle "all".

  // Let's try to fetch using a loop for now or just display structure if no endpoint.
  // Actually, I'll add a temporary useEffect to fetch data for the first school found in schoolData as a demo,
  // or better, I will assume I need to Create/Update an endpoint for "All Thawabet".

  // Since I am an autonomous agent, I should probably add the endpoint to backend first to support this feature properly.
  // User said: "Modify the table to bring all fields... make a page... put all data".

  // Plan:
  // 1. Backend: Add fetchAllThawabetCtrl
  // 2. Frontend: Fetch from this endpoint.

  useEffect(() => {
    const fetchData = async () => {
      if (!token) return;
      setLoading(true);
      setError(null);
      try {
        const response = await axios.get(
          `https://api.alfares.cloud/api/alfares/fetchAllThawabet/${token}`
        );
        console.log("Thawabet Data:", response.data.thawabetData); // 🔍 طباعة البيانات في الكونسول
        setData(response.data.thawabetData || []);
      } catch (err) {
        console.error(err);
        const errorMsg =
          err.response?.data?.message ||
          err.response?.data?.error ||
          err.message ||
          "فشل في جلب البيانات";
        setError(`خطأ: ${errorMsg}. (تأكد من تحديث السيرفر)`);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [token]);

  if (loading)
    return <div className="p-4 text-center">جاري تحميل البيانات...</div>;

  return (
    <div className="school-constants-page p-4" dir="rtl">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-800">ثوابت المدارس</h1>
        <button
          onClick={() => window.location.reload()}
          className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded shadow transition"
        >
          تحديث الصفحة
        </button>
      </div>

      {error && (
        <div
          className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4"
          role="alert"
        >
          <strong className="font-bold ml-2">حدث خطأ!</strong>
          <span className="block sm:inline">{error}</span>
        </div>
      )}

      <div className="overflow-x-auto bg-white rounded-lg shadow">
        <table className="min-w-full table-auto">
          <thead className="bg-gray-100 text-gray-700">
            <tr>
              <th className="px-4 py-2 border">#</th>
              <th className="px-4 py-2 border">الرقم الوزاري</th>
              <th className="px-4 py-2 border">اسم المدرسة</th>
              <th className="px-4 py-2 border">رقم المدرسة</th>
              <th className="px-4 py-2 border">اسم المديرية</th>
              <th className="px-4 py-2 border">اسم المدير</th>
              <th className="px-4 py-2 border">كل البيانات (للتصحيح)</th>
            </tr>
          </thead>
          <tbody>
            {data.map((row, index) => (
              <tr key={index} className="hover:bg-gray-50">
                <td className="px-4 py-2 border text-center">{index + 1}</td>
                <td className="px-4 py-2 border text-center font-bold">
                  {row.school_rgmwtni || row.rgmw || "---"}
                </td>
                <td className="px-4 py-2 border text-center">
                  {row.school_name || "---"}
                </td>
                <td className="px-4 py-2 border text-center">
                  {row.school_number || "---"}
                </td>
                <td className="px-4 py-2 border text-center">
                  {row.AlModeeria_name_a || row.Modeeria_name_a || "---"}
                </td>
                <td className="px-4 py-2 border text-center">
                  {row.headMaster_Name || "---"}
                </td>
                <td className="px-4 py-2 border text-left text-xs" dir="ltr">
                  <pre className="whitespace-pre-wrap h-20 overflow-y-auto bg-gray-50 p-2">
                    {JSON.stringify(row, null, 2)}
                  </pre>
                </td>
              </tr>
            ))}
            {data.length === 0 && (
              <tr>
                <td colSpan="6" className="px-4 py-8 text-center text-gray-500">
                  لا توجد بيانات
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default SchoolConstants;
